import { Circle, Grid, GridItem, Icon, Text } from '@chakra-ui/react';
import { Currency } from 'components/Number';
import Card from 'components/card/Card';
import PropTypes from 'prop-types';
import { FaDollarSign } from 'react-icons/fa6';
import { MdRedo } from 'react-icons/md';

const cardProps = {
  px: '30px',
  py: '20px',
  h: '100%',
};

const titleProps = {
  color: 'secondaryGray.600',
  fontSize: 'sm',
};

const valueProps = {
  fontSize: 24,
  fontWeight: 800,
};

const KpiIcon = ({ icon, iconSize }) => (
  <Circle
    size={'60px'}
    bgGradient={'linear-gradient(-260deg, #4318ff 3.49%,  #868cff 96.73%)'}
    color={'white'}
    position={'absolute'}
    top={'-40px'}
    left={'30px'}
    zIndex={1}
  >
    {' '}
    <Icon as={icon} boxSize={iconSize} />
  </Circle>
);

const currencyOrNA = (value) =>
  value === null || value === undefined ? (
    'N/A'
  ) : (
    <Currency value={value} maximumFractionDigits={2} />
  );

const OperationsKpisOld = ({
  totalPurchase,
  totalSold,
  principalDueOutstanding,
  feeDueOutstanding,
  principalRepaid,
  feeRepaid,
  totalFacilityFee,
  repaidFacilityFee,
}) => (
  <Grid
    templateRows="repeat(2, auto)"
    templateColumns="repeat(4, auto)"
    gridGap={'20px 40px'}
    w={'max'}
  >
    <GridItem>
      <Card {...cardProps} pl={'100px'}>
        <Text {...titleProps}>Total Purchase Price</Text>
        <Text {...valueProps}>
          <Currency value={totalPurchase} maximumFractionDigits={2} />
        </Text>
      </Card>
    </GridItem>
    <GridItem position={'relative'}>
      <Card {...cardProps}>
        <Text {...titleProps}>Principal Due Outstanding</Text>
        <Text {...valueProps}>
          <Currency value={principalDueOutstanding} maximumFractionDigits={2} />
        </Text>
      </Card>
    </GridItem>
    <GridItem position={'relative'}>
      <Card {...cardProps} pl={'100px'}>
        <Text {...titleProps}>Principal Repaid</Text>
        <Text {...valueProps}>
          <Currency value={principalRepaid} maximumFractionDigits={2} />
        </Text>
      </Card>
    </GridItem>
    <GridItem>
      <Card {...cardProps}>
        <Text {...titleProps}>Total Facility Fee</Text>
        <Text {...valueProps}>
          <Currency value={totalFacilityFee ?? 0} maximumFractionDigits={2} />
        </Text>
      </Card>
    </GridItem>
    <GridItem position={'relative'}>
      <KpiIcon icon={FaDollarSign} iconSize={'24px'} />
      <Card {...cardProps} pl={'100px'}>
        <Text {...titleProps}>Total Amount Sold</Text>
        <Text {...valueProps}>
          <Currency value={totalSold} maximumFractionDigits={2} />
        </Text>
      </Card>
    </GridItem>
    <GridItem>
      <Card {...cardProps}>
        <Text {...titleProps}>Fee Due Outstanding</Text>
        <Text {...valueProps}>
          <Currency value={feeDueOutstanding} maximumFractionDigits={2} />
        </Text>
      </Card>
    </GridItem>
    <GridItem position={'relative'}>
      <KpiIcon icon={MdRedo} iconSize={'30px'} />
      <Card {...cardProps} pl={'100px'}>
        <Text {...titleProps}>Fee Repaid</Text>
        <Text {...valueProps}>
          <Currency value={feeRepaid} maximumFractionDigits={2} />
        </Text>
      </Card>
    </GridItem>
    <GridItem>
      <Card {...cardProps}>
        <Text {...titleProps}>Repaid Facility Fee</Text>
        <Text {...valueProps}>
          <Currency value={repaidFacilityFee ?? 0} maximumFractionDigits={2} />
        </Text>
      </Card>
    </GridItem>
  </Grid>
);

const OperationsKpisNew = ({
  totalGrowthInvestment,
  currentReturns,
  totalExpectedReturns,
  currentExposure,
  cumulativeFee,
  expectedFutureFee,
  totalFacilityFee,
  repaidFacilityFee,
}) => (
  <Grid
    templateRows="repeat(2, auto)"
    templateColumns="repeat(4, auto)"
    gridGap={'20px 40px'}
    w={'max'}
  >
    <GridItem>
      <Card {...cardProps} pl={'100px'}>
        <Text {...titleProps}>Total Growth Investment</Text>
        <Text {...valueProps}>{currencyOrNA(totalGrowthInvestment)}</Text>
      </Card>
    </GridItem>
    <GridItem position={'relative'}>
      <Card {...cardProps}>
        <Text {...titleProps}>Current Exposure</Text>
        <Text {...valueProps}>{currencyOrNA(currentExposure)}</Text>
      </Card>
    </GridItem>
    <GridItem position={'relative'}>
      <Card {...cardProps} pl={'100px'}>
        <Text {...titleProps}>Cumulative Fee (fees till today)</Text>
        <Text {...valueProps}>{currencyOrNA(cumulativeFee)}</Text>
      </Card>
    </GridItem>
    <GridItem>
      <Card {...cardProps}>
        <Text {...titleProps}>Total Facility Fee</Text>
        <Text {...valueProps}>
          <Currency value={totalFacilityFee ?? 0} maximumFractionDigits={2} />
        </Text>
      </Card>
    </GridItem>
    <GridItem position={'relative'}>
      <KpiIcon icon={FaDollarSign} iconSize={'24px'} />
      <Card {...cardProps} pl={'100px'}>
        <Text {...titleProps}>Total Expected Returns</Text>
        <Text {...valueProps}>{currencyOrNA(totalExpectedReturns)}</Text>
      </Card>
    </GridItem>
    <GridItem>
      <Card {...cardProps}>
        <Text {...titleProps}>Current Returns</Text>
        <Text {...valueProps}>{currencyOrNA(currentReturns)}</Text>
      </Card>
    </GridItem>
    <GridItem position={'relative'}>
      <KpiIcon icon={MdRedo} iconSize={'30px'} />
      <Card {...cardProps} pl={'100px'}>
        <Text {...titleProps}>Expected Future Fee</Text>
        <Text {...valueProps}>{currencyOrNA(expectedFutureFee)}</Text>
      </Card>
    </GridItem>
    <GridItem>
      <Card {...cardProps}>
        <Text {...titleProps}>Repaid Facility Fee</Text>
        <Text {...valueProps}>
          <Currency value={repaidFacilityFee ?? 0} maximumFractionDigits={2} />
        </Text>
      </Card>
    </GridItem>
  </Grid>
);

export const ASSET_TAPE_VIEW = {
  NEW: 'new',
  OLD: 'old',
};

const OperationsKpis = ({ view = ASSET_TAPE_VIEW.NEW, ...props }) => {
  if (view === ASSET_TAPE_VIEW.OLD) {
    return <OperationsKpisOld {...props} />;
  }
  return <OperationsKpisNew {...props} />;
};

OperationsKpis.propTypes = {
  view: PropTypes.oneOf([ASSET_TAPE_VIEW.NEW, ASSET_TAPE_VIEW.OLD]),
  totalPurchase: PropTypes.number,
  totalSold: PropTypes.number,
  amountDueOutstanding: PropTypes.number,
  principalDueOutstanding: PropTypes.number,
  feeDueOutstanding: PropTypes.number,
  principalRepaid: PropTypes.number,
  feeRepaid: PropTypes.number,
  totalFacilityFee: PropTypes.number,
  repaidFacilityFee: PropTypes.number,
  totalGrowthInvestment: PropTypes.number,
  currentReturns: PropTypes.number,
  totalExpectedReturns: PropTypes.number,
  currentExposure: PropTypes.number,
  cumulativeFee: PropTypes.number,
  expectedFutureFee: PropTypes.number,
};

export default OperationsKpis;
